if (!customElements.get('vl-pdp-media-testimonials')) {
  class VLPDPMediaTestimonials extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.isInitialized = false;
      this.abortController = null;
      this.instantFrame = null;
      this.pointerState = null;
      this.pauseReasons = new Set();
      this.intersectionObserver = null;

      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handlePaginationClick = this.handlePaginationClick.bind(this);
      this.handleViewportKeydown = this.handleViewportKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerEnd = this.handlePointerEnd.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleProgressAnimationEnd = this.handleProgressAnimationEnd.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.viewport = this.querySelector('[data-vl-pdp-media-testimonials-viewport]');
      this.slides = [...this.querySelectorAll('[data-vl-pdp-media-testimonials-slide]')];
      this.paginationItems = [
        ...this.querySelectorAll('[data-vl-pdp-media-testimonials-pagination]'),
      ];
      this.logoOptions = [
        ...this.querySelectorAll('[data-vl-pdp-media-testimonials-logo-option]'),
      ];
      this.content = this.querySelector('[data-vl-pdp-media-testimonials-content]');
      this.previousButton = this.querySelector('[data-vl-pdp-media-testimonials-previous]');
      this.nextButton = this.querySelector('[data-vl-pdp-media-testimonials-next]');
      this.status = this.querySelector('[data-vl-pdp-media-testimonials-status]');

      if (!this.viewport || this.slides.length === 0) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.previousButton?.addEventListener('click', this.handlePreviousClick, { signal });
      this.nextButton?.addEventListener('click', this.handleNextClick, { signal });
      this.addEventListener('click', this.handlePaginationClick, { signal });
      this.viewport.addEventListener('keydown', this.handleViewportKeydown, { signal });
      this.viewport.addEventListener('pointerdown', this.handlePointerDown, { signal });
      this.viewport.addEventListener('pointermove', this.handlePointerMove, { signal });
      this.viewport.addEventListener('pointerup', this.handlePointerEnd, { signal });
      this.viewport.addEventListener('pointercancel', this.handlePointerEnd, { signal });
      this.addEventListener('animationend', this.handleProgressAnimationEnd, { signal });
      this.content?.addEventListener('mouseenter', this.handleMouseEnter, { signal });
      this.content?.addEventListener('mouseleave', this.handleMouseLeave, { signal });
      this.addEventListener('focusin', this.handleFocusIn, { signal });
      this.addEventListener('focusout', this.handleFocusOut, { signal });
      document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

      if (document.hidden) this.setPauseReason('document-hidden', true);

      if ('IntersectionObserver' in window) {
        this.setPauseReason('out-of-view', true);
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => this.setPauseReason('out-of-view', !entry?.isIntersecting),
          { threshold: 0.15 },
        );
        this.intersectionObserver.observe(this);
      }

      const initialIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.showSlide(initialIndex >= 0 ? initialIndex : 0, false, false);
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.abortController?.abort();
      this.intersectionObserver?.disconnect();
      if (this.instantFrame !== null) cancelAnimationFrame(this.instantFrame);

      this.abortController = null;
      this.intersectionObserver = null;
      this.instantFrame = null;
      this.pointerState = null;
      this.pauseReasons.clear();
      this.isInitialized = false;
    }

    handlePreviousClick() {
      this.showSlide(this.currentIndex - 1);
    }

    handleNextClick() {
      this.showSlide(this.currentIndex + 1);
    }

    handlePaginationClick(event) {
      const item = event.target.closest(
        '[data-vl-pdp-media-testimonials-pagination], [data-vl-pdp-media-testimonials-logo-option]',
      );
      if (!item || !this.contains(item)) return;

      this.showSlide(Number(item.dataset.slideIndex));
    }

    handleViewportKeydown(event) {
      if (event.target !== this.viewport) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handlePreviousClick();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleNextClick();
      }
    }

    handlePointerDown(event) {
      if (this.slides.length < 2) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.pointerState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        currentX: event.clientX,
        currentY: event.clientY,
      };

      this.setPauseReason('pointer-interaction', true);

      this.viewport.setPointerCapture?.(event.pointerId);
    }

    handlePointerMove(event) {
      if (!this.pointerState || event.pointerId !== this.pointerState.pointerId) return;

      this.pointerState.currentX = event.clientX;
      this.pointerState.currentY = event.clientY;

      const deltaX = event.clientX - this.pointerState.startX;
      const deltaY = event.clientY - this.pointerState.startY;
      this.classList.toggle('is-dragging', Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY));
    }

    handlePointerEnd(event) {
      if (!this.pointerState || event.pointerId !== this.pointerState.pointerId) return;

      const endX = Number.isFinite(event.clientX) ? event.clientX : this.pointerState.currentX;
      const endY = Number.isFinite(event.clientY) ? event.clientY : this.pointerState.currentY;
      const deltaX = endX - this.pointerState.startX;
      const deltaY = endY - this.pointerState.startY;
      const threshold = Math.min(72, Math.max(40, this.viewport.clientWidth * 0.12));

      if (
        event.type !== 'pointercancel' &&
        Math.abs(deltaX) >= threshold &&
        Math.abs(deltaX) > Math.abs(deltaY)
      ) {
        if (deltaX < 0) {
          this.handleNextClick();
        } else {
          this.handlePreviousClick();
        }
      }

      if (this.viewport.hasPointerCapture?.(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }
      this.classList.remove('is-dragging');
      this.pointerState = null;
      this.setPauseReason('pointer-interaction', false);
    }

    handleProgressAnimationEnd(event) {
      if (event.animationName !== 'vl-pdp-media-testimonials-progress') return;
      if (!event.target.closest('[data-vl-pdp-media-testimonials-pagination].is-active')) return;
      if (this.pauseReasons.size > 0 || this.prefersReducedMotion) return;

      this.showSlide(this.currentIndex + 1);
    }

    handleMouseEnter() {
      this.setPauseReason('hover', true);
    }

    handleMouseLeave() {
      this.setPauseReason('hover', false);
    }

    handleFocusIn(event) {
      const isKeyboardFocus = event.target?.matches?.(':focus-visible') ?? false;
      this.setPauseReason('focus', isKeyboardFocus);
    }

    handleFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.setPauseReason('focus', false);
    }

    handleVisibilityChange() {
      this.setPauseReason('document-hidden', document.hidden);
    }

    setPauseReason(reason, shouldPause) {
      if (shouldPause) {
        this.pauseReasons.add(reason);
      } else {
        this.pauseReasons.delete(reason);
      }

      this.classList.toggle('is-autoplay-paused', this.pauseReasons.size > 0);
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;

      const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (index === -1) return;

      this.showSlide(index, false);
    }

    showSlide(index, animate = true, announce = true) {
      const slideCount = this.slides.length;
      if (slideCount === 0) return;

      const targetIndex = ((index % slideCount) + slideCount) % slideCount;
      const shouldAnimate = animate && !this.prefersReducedMotion;

      if (!shouldAnimate) {
        this.classList.add('is-instant');
        if (this.instantFrame !== null) cancelAnimationFrame(this.instantFrame);
      }

      this.currentIndex = targetIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === targetIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.inert = !isActive;
      });

      this.paginationItems.forEach((item, itemIndex) => {
        const isActive = itemIndex === targetIndex;
        item.classList.toggle('is-active', isActive);

        if (isActive) {
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      this.logoOptions.forEach((item, itemIndex) => {
        const isActive = itemIndex === targetIndex;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-pressed', String(isActive));
      });

      if (announce && this.status) {
        this.status.textContent = `Showing media testimonial ${targetIndex + 1} of ${slideCount}`;
      }

      if (!shouldAnimate) {
        this.instantFrame = requestAnimationFrame(() => {
          this.classList.remove('is-instant');
          this.instantFrame = null;
        });
      }
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('vl-pdp-media-testimonials', VLPDPMediaTestimonials);
}
